(() => {
  // 書類選考で顔と全身が判別できれば足りる解像度。2 枚で 1MB 前後に収まる
  const MAX_EDGE = 1600;
  const JPEG_QUALITY = 0.85;
  // SSGform は 1 回の送信全体で約 5MB まで。テキストと multipart の分を残す
  const TOTAL_LIMIT = 4.5 * 1024 * 1024;

  /**
   * @param {File} file
   * @returns {Promise<HTMLImageElement>}
   */
  function loadImage(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(url);
        resolve(image);
      };
      image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('decode failed'));
      };
      image.src = url;
    });
  }

  /**
   * 長辺を MAX_EDGE 以下の JPEG にする。デコードできない形式は元のファイルを返す
   * @param {File} file
   * @returns {Promise<File>}
   */
  async function resizeImage(file) {
    let image;
    try {
      image = await loadImage(file);
    } catch {
      return file;
    }

    const scale = Math.min(1, MAX_EDGE / Math.max(image.naturalWidth, image.naturalHeight));
    if (scale === 1 && file.type === 'image/jpeg') return file;

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(image.naturalWidth * scale);
    canvas.height = Math.round(image.naturalHeight * scale);
    const context = canvas.getContext('2d');
    if (!context) return file;
    // JPEG は透過を持てず、塗らないと透明部分が黒になる
    context.fillStyle = '#fff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    if (!blob || blob.size >= file.size) return file;

    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified });
  }

  class CRecruitForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.error = this.querySelector('[data-recruit-form-error]');
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.consent = this.form.querySelector('[data-recruit-form-consent]');
      this.pending = new Set();

      // JS が動くときだけ独自の表示に切り替える。動かなければブラウザ標準の検証が残る
      this.form.noValidate = true;
      this.updateSubmitState();

      this.onChange = this.handleChange.bind(this);
      this.onInput = this.handleInput.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      this.form.addEventListener('change', this.onChange);
      this.form.addEventListener('input', this.onInput);
      this.form.addEventListener('submit', this.onSubmit);
    }

    disconnectedCallback() {
      this.querySelectorAll('[data-recruit-form-preview]').forEach((preview) => this.revokePreview(preview));
      this.form?.removeEventListener('change', this.onChange);
      this.form?.removeEventListener('input', this.onInput);
      this.form?.removeEventListener('submit', this.onSubmit);
    }

    /** @param {Event} event */
    handleInput(event) {
      const control = event.target;
      if (control instanceof HTMLElement && control.getAttribute('aria-invalid') === 'true') {
        this.validateControl(control);
      }
    }

    /** @param {Event} event */
    async handleChange(event) {
      const input = event.target;
      if (input === this.consent) this.updateSubmitState();
      if (input instanceof HTMLElement && input.getAttribute('aria-invalid') === 'true') {
        this.validateControl(input);
      }

      if (!(input instanceof HTMLInputElement) || input.type !== 'file') return;
      const file = input.files?.[0];
      if (!file) {
        this.clearPreview(input);
        return;
      }
      if (!file.type.startsWith('image/')) {
        this.renderPreview(input);
        return;
      }

      this.renderPreview(input, true);
      const task = resizeImage(file)
        .then((resized) => {
          // 選び直された場合は古い結果で上書きしない
          if (input.files?.[0] !== file || resized === file) return;
          const transfer = new DataTransfer();
          transfer.items.add(resized);
          input.files = transfer.files;
        })
        // 縮小できなくても元のファイルで送れるため、失敗は握りつぶしてプレビューだけ戻す
        .catch(() => {})
        .finally(() => {
          if (input.files?.[0]) this.renderPreview(input);
        });

      this.pending.add(task);
      this.setBusy(true);
      try {
        await task;
      } finally {
        this.pending.delete(task);
        if (this.pending.size === 0) this.setBusy(false);
      }
      this.showError('');
    }

    /**
     * 選んだ写真（縮小後）をサムネイルで出す。表示できない形式はファイル名だけ出す
     * @param {HTMLInputElement} input
     * @param {boolean} [loading]
     */
    renderPreview(input, loading = false) {
      const preview = input.closest('[data-recruit-form-field]')?.querySelector('[data-recruit-form-preview]');
      const file = input.files?.[0];
      if (!preview || !file) return;

      this.revokePreview(preview);
      preview.replaceChildren();
      preview.hidden = false;

      if (loading) {
        preview.textContent = this.dataset.previewLoading ?? '';
        return;
      }

      const image = document.createElement('img');
      const url = URL.createObjectURL(file);
      preview.dataset.objectUrl = url;
      image.src = url;
      image.alt = input.dataset.previewAlt ?? '';
      image.className = 'c_p-metaobject-recruit-job__preview-image';
      image.addEventListener('error', () => image.replaceWith(document.createTextNode(file.name)), { once: true });

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'c_p-metaobject-recruit-job__preview-remove link';
      remove.textContent = this.dataset.previewRemove ?? '';
      remove.addEventListener('click', () => {
        input.value = '';
        this.clearPreview(input);
        if (input.getAttribute('aria-invalid') === 'true') this.validateControl(input);
        input.focus();
      });

      preview.append(image, remove);
    }

    /** @param {HTMLInputElement} input */
    clearPreview(input) {
      const preview = input.closest('[data-recruit-form-field]')?.querySelector('[data-recruit-form-preview]');
      if (!preview) return;
      this.revokePreview(preview);
      preview.replaceChildren();
      preview.hidden = true;
    }

    /** @param {HTMLElement} preview */
    revokePreview(preview) {
      if (!preview.dataset.objectUrl) return;
      URL.revokeObjectURL(preview.dataset.objectUrl);
      delete preview.dataset.objectUrl;
    }

    /** @param {SubmitEvent} event */
    handleSubmit(event) {
      if (this.pending.size > 0) {
        event.preventDefault();
        return;
      }

      const invalid = [...this.form.elements].filter(
        (control) => control instanceof HTMLElement && 'validity' in control && !this.validateControl(control),
      );
      if (invalid.length > 0) {
        event.preventDefault();
        invalid[0].focus();
        return;
      }

      const total = [...this.form.querySelectorAll('input[type="file"]')]
        .flatMap((input) => [...(input.files ?? [])])
        .reduce((sum, file) => sum + file.size, 0);

      if (total > TOTAL_LIMIT) {
        event.preventDefault();
        this.showError(this.dataset.sizeError ?? '');
      }
    }

    /**
     * 項目ごとのエラー文を出し分ける。妥当なら消す
     * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} control
     * @returns {boolean}
     */
    validateControl(control) {
      if (!control.willValidate) return true;
      const field = control.closest('[data-recruit-form-field]');
      if (!field) return control.checkValidity();

      let message = '';
      if (control.validity.valueMissing) {
        if (control === this.consent) message = this.dataset.messageConsent;
        else if (control.type === 'file') message = this.dataset.messageFile;
        else if (control instanceof HTMLSelectElement) message = this.dataset.messageSelect;
        else message = this.dataset.messageRequired;
      } else if (!control.validity.valid) {
        message = this.dataset.messageFormat;
      }

      let note = field.querySelector('[data-recruit-form-field-error]');
      if (message) {
        if (!note) {
          note = document.createElement('p');
          note.className = 'c_p-metaobject-recruit-job__field-error';
          note.id = `${control.id}-error`;
          note.dataset.recruitFormFieldError = '';
          field.append(note);
        }
        note.textContent = message ?? '';
        control.setAttribute('aria-invalid', 'true');
        control.setAttribute('aria-describedby', note.id);
        return false;
      }

      note?.remove();
      control.removeAttribute('aria-invalid');
      control.removeAttribute('aria-describedby');
      return true;
    }

    updateSubmitState() {
      if (!this.submitButton || !this.consent) return;
      // disabled だと押しても何も起きず、未入力の項目を案内できないため aria-disabled で見た目だけ変える
      this.submitButton.setAttribute('aria-disabled', String(!this.consent.checked));
    }

    /** @param {boolean} busy */
    setBusy(busy) {
      if (!this.submitButton) return;
      this.submitButton.disabled = busy;
      this.submitButton.setAttribute('aria-busy', String(busy));
    }

    /** @param {string} message */
    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = message === '';
    }
  }

  if (!customElements.get('c-recruit-form')) {
    customElements.define('c-recruit-form', CRecruitForm);
  }
})();
